import { useState } from 'react';
import { abandonMatch } from '../../app/transitions';
import { isInMatch } from '../../domain/machine';
import { SheetB } from '../SheetB';
import type { MatchRow } from '../../types';

/**
 * B-11 — the band's `⋯` overflow (design spec §2.4; scenario 19): **guarded Abandon, and
 * nothing else destructive**.
 *
 * The guard is a **confirm, deliberately not a hold** (§2.4: *"`abandonMatch` — anchor is
 * `now`, no hold"*): SES-1's confirming hold exists for the four clock-anchoring transitions,
 * whose anchor every later minute derives from; Abandon writes `fullTimeTs = now` inside the
 * transaction and **retains every row already captured** — destructive of the session's
 * continuation, never of the data — so the right guard is asking, in words that say exactly
 * that. Teaching a hold here would blur what a hold means on this chrome (the whistle owns it).
 *
 * Abandon renders only in-match. The hiding is honesty, not enforcement — the machine has no
 * `preMatch → Abandon` row and rejects it regardless (`ERR_ILLEGAL_TRANSITION`); pre-kickoff
 * the sheet says plainly that nothing lives here yet.
 *
 * Built over `SheetB` (`<dialog>`/`showModal()`, the floor-safe modal — B's one sheet
 * primitive, reused rather than re-made) with SheetB's own control vocabulary. Inerting the
 * page under it is correct for this content: everything here either closes the sheet or ends
 * the match (§2.3a's admission rule, carried from A's overflow — not a capture action, not
 * time-critical, terminates capture).
 */

export interface OverflowSheetBProps {
  readonly match: MatchRow;
  readonly onClose: () => void;
  /** Called after Abandon commits — the host re-reads the row; the chrome's hand-off follows
   *  from the committed `fullTime` state (Abandon clears `activeMatch` by engine law). */
  readonly onTransitioned: () => void;
}

export function OverflowSheetB({ match, onClose, onTransitioned }: OverflowSheetBProps) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abandon = (): void => {
    if (busy) return;
    setBusy(true);
    setError(null);
    void abandonMatch(match.id).then(
      (result) => {
        setBusy(false);
        if (!result.ok) {
          // A rejection is a returned value (the bus contract) — rendered, never thrown.
          setError(result.message);
          return;
        }
        onClose();
        onTransitioned();
      },
      (cause: unknown) => {
        setBusy(false);
        setError(cause instanceof Error ? cause.message : String(cause));
      },
    );
  };

  return (
    <SheetB
      titleId="bandb-overflow-title"
      title="More"
      testId="bandb-overflow"
      onClose={onClose}
      actions={
        <button
          type="button"
          className="sheet-b__secondary"
          data-testid="bandb-overflow-close"
          onClick={onClose}
        >
          Close
        </button>
      }
    >
      {isInMatch(match.state) && !confirming && (
        <button
          type="button"
          className="sheet-b__destructive"
          data-testid="bandb-abandon"
          onClick={() => {
            setConfirming(true);
          }}
        >
          Abandon match
        </button>
      )}

      {isInMatch(match.state) && confirming && (
        <div className="sheet-b__danger" data-testid="bandb-abandon-confirm">
          {/* Scenario 19 — the confirm NAMES what it does: ends the session, keeps the data. */}
          <p className="sheet-b__problem">
            Abandon this match? <strong>Everything captured so far is kept</strong> — the match
            ends now, at the current minute, and moves to full time. There is no way back into
            the half.
          </p>
          <button
            type="button"
            className="sheet-b__quiet"
            data-testid="bandb-abandon-keep"
            onClick={() => {
              setConfirming(false);
            }}
            disabled={busy}
          >
            Keep capturing
          </button>
          <button
            type="button"
            className="sheet-b__destructive"
            data-testid="bandb-abandon-commit"
            onClick={abandon}
            disabled={busy}
          >
            Abandon
          </button>
        </div>
      )}

      {!isInMatch(match.state) && (
        <p className="sheet-b__hint" data-testid="bandb-overflow-empty">
          Nothing lives here before kickoff — the whistle above starts the match.
        </p>
      )}

      {error !== null && (
        <p className="sheet-b__problem" role="alert" data-testid="bandb-overflow-error">
          {error}
        </p>
      )}
    </SheetB>
  );
}
